import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import {
  BackButton,
  DownloadButton,
  EditButton,
} from "../../../../Components/Utils/Buttons";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { CopyButton } from "../../../../Components/Others/PremiumTable";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import { useDeleteData } from "../../../../Components/Hooks/useDeleteData";
import { GetAccess, GetRole } from "../../../../Components/Utils/roles";
import NuAmount from "../../../../Components/Utils/NuAmount";
import NuDate from "../../../../Components/Utils/NuDate";
import axiosInstance from "../../../../Services/axiosCommon";

const ViewProformaInvoice = () => {
  const { piViewId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stateData = location.state?.data;
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const [data, setData] = useState(stateData || null);
  const [loading, setLoading] = useState(!stateData);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [viewPDF, setViewPDF] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const canEdit = GetAccess("proforma_invoice_edit") || GetRole();
  const canDelete = GetAccess("proforma_invoice_delete") || GetRole();

  const [vatRate, setVatRate] = useState(15);

  useEffect(() => {
    axiosInstance
      .get("/vat/")
      .then((res) => {
        const rate = res.data?.data?.vatRate ?? res.data?.vatRate;
        if (rate !== undefined && rate !== null && !isNaN(Number(rate))) {
          setVatRate(Number(rate));
        }
      })
      .catch((err) => {
        console.error("Error fetching VAT rate:", err);
      });
  }, []);

  const fetchPiDetails = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/proforma-invoice/${piViewId}`);
      if (res.data?.data) {
        setData(res.data.data);
      } else if (res.data) {
        setData(res.data);
      }
    } catch (err) {
      console.error("Error fetching PI details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (piViewId) {
      fetchPiDetails();
    }
  }, [piViewId]);

  useEffect(() => {
    let active = true;

    const fetchPdfBlob = async () => {
      setPdfLoading(true);
      try {
        const response = await axiosInstance.get(
          `/proforma-invoice/download-pdf/${piViewId}`,
          {
            responseType: "blob",
          }
        );
        if (!active) return;
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const pdfUrl = window.URL.createObjectURL(pdfBlob);
        setViewPDF(pdfUrl);
      } catch (error) {
        console.error("Error loading PDF preview:", error);
      } finally {
        if (active) {
          setPdfLoading(false);
        }
      }
    };

    if (showPdfModal) {
      fetchPdfBlob();
    }

    return () => {
      active = false;
      setViewPDF((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return null;
      });
      setPdfLoading(false);
    };
  }, [showPdfModal, piViewId]);

  const { handle: handleDeleteData } = useDeleteData(() => {
    navigate("/sales/proforma-invoice", {
      state: { currentPage: pageIndex, searchQuery },
    });
  });

  const handleConfirmDelete = () => {
    handleDeleteData(`/proforma-invoice/${piViewId}`, setDeleteConfirmOpen);
  };

  if (loading) {
    return (
      <div className="w-full h-full py-8 px-4 flex flex-col items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#048178] border-t-transparent rounded-full animate-spin mb-3"></div>
        <p className="text-sm font-medium text-gray-500">
          Loading Proforma Invoice Details...
        </p>
      </div>
    );
  }

  const piRef = data?.piNumber || data?.ref || `PI-${piViewId}`;
  const productList =
    data?.piProducts || data?.products || data?.pi_products || [];

  const subTotal =
    data?.subTotal ??
    productList.reduce(
      (acc, p) =>
        acc +
        (Number(p.quantity) || 0) * (Number(p.unitPrice ?? p.price) || 0),
      0
    );
  const discount = Number(data?.discountAmount) || 0;
  const transportation = Number(data?.transportationCharges) || 0;
  const taxableAmount = Math.max(0, subTotal - discount + transportation);
  const effectiveVatRate =
    data?.vatRate !== undefined && data?.vatRate !== null
      ? Number(data.vatRate)
      : vatRate;
  const vatAmount =
    data?.vatAmount ??
    (data?.vatApplicable ? taxableAmount * (effectiveVatRate / 100) : 0);
  const grandTotal =
    data?.grandTotal ??
    data?.totalAmount ??
    data?.totalPrice ??
    taxableAmount + vatAmount;
  const selectedCurrency = data?.currencyName || data?.currency || "SAR";

  const formattedAddress = (() => {
    const isInvalid = (val) =>
      !val ||
      typeof val !== "string" ||
      ["none", "null", "undefined", "-", "n/a", ""].includes(val.trim().toLowerCase());

    const addr = data?.customerAddress || data?.address || data?.customerAddressDetails;

    if (typeof addr === "string") {
      return isInvalid(addr) ? null : addr.trim();
    }

    if (addr && typeof addr === "object") {
      const parts = [
        addr.billingAddress1,
        addr.billingAddress2,
        addr.billing_city,
        addr.billing_state,
        addr.billing_country,
        addr.billing_postal_code
      ]
        .filter((p) => p && !isInvalid(String(p)))
        .map((p) => String(p).trim());

      if (parts.length > 0) return parts.join(", ");
    }

    // Fallback to customer object address
    const cust = data?.customer;
    if (cust) {
      if (typeof cust.address === "string" && !isInvalid(cust.address)) {
        return cust.address.trim();
      }
      if (Array.isArray(cust.addresses) && cust.addresses.length > 0) {
        const firstAddr = cust.addresses[0];
        const parts = [
          firstAddr.address1 || firstAddr.streetName || firstAddr.address,
          firstAddr.address2,
          firstAddr.city || firstAddr.district,
          firstAddr.state,
          firstAddr.country,
          firstAddr.postalCode,
        ]
          .filter((p) => p && !isInvalid(String(p)))
          .map((p) => String(p).trim());

        if (parts.length > 0) return parts.join(", ");
      }
    }

    return null;
  })();

  return (
    <div className="w-full h-full py-2 px-2 lg:px-4">
      {/* Top Header / Actions Bar */}
      <div className="w-full flex flex-col md:flex-row justify-between items-stretch md:items-center gap-2 md:gap-3 mb-3">
        <div className="w-full md:w-auto flex justify-between items-center gap-2">
          <Breadcrumb
            items={[
              { name: "Sales", link: "/sales/proforma-invoice" },
              { name: "Proforma Invoice", link: "/sales/proforma-invoice" },
              { name: piRef },
            ]}
          />
          <div className="flex md:hidden items-center gap-2">
            <Link
              to="/sales/proforma-invoice"
              state={{ currentPage: pageIndex, searchQuery }}
            >
              <BackButton link={true} />
            </Link>
            <DownloadButton
              handleClick={() => setShowPdfModal(true)}
              label="Download as PDF"
            />
            {canEdit && (
              <Link
                to={`/sales/proforma-invoice/edit/${piViewId}`}
                state={{
                  data,
                  from: `/sales/proforma-invoice/view/${piViewId}`,
                  pageIndex,
                  searchQuery,
                }}
              >
                <EditButton height="8" />
              </Link>
            )}
            {canDelete && (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="px-2.5 h-8 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg inline-flex items-center justify-center gap-1 transition-colors cursor-pointer"
                title="Delete Proforma Invoice"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/sales/proforma-invoice"
            state={{ currentPage: pageIndex, searchQuery }}
          >
            <BackButton link={true} />
          </Link>
          {/* <DownloadButton
            handleClick={() => setShowPdfModal(true)}
            label="Download as PDF"
          /> */}
          {canEdit && (
            <Link
              to={`/sales/proforma-invoice/edit/${piViewId}`}
              state={{
                data,
                from: `/sales/proforma-invoice/view/${piViewId}`,
                pageIndex,
                searchQuery,
              }}
            >
              <EditButton height="8" />
            </Link>
          )}
          {/* {canDelete && (
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-3 h-8 text-xs 2xl:text-sm font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg inline-flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
              title="Delete Proforma Invoice"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span>Delete</span>
            </button>
          )} */}
        </div>
      </div>

      {/* Main 2-Column Standard View Layout */}
      <div className="w-full mx-auto lg:h-[calc(100vh-125px)] overflow-y-auto rounded-md p-2 flex flex-col lg:flex-row gap-3 text-[#111537]">
        {/* Left Column (70%) */}
        <div className="w-full lg:w-[70%] bg-white rounded-lg drop-shadow-lg py-1 overflow-hidden flex flex-col">
          {/* Top Info Bar */}
          <div className="w-[98%] bg-white px-2 mx-auto border border-gray py-2 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 z-10">
            <div className="grid items-center w-full sm:w-[80%]">
              <div className="flex text-[14px] mb-1 text-[#111537] font-semibold items-center flex-wrap gap-2">
                <p className="break-words">{piRef}</p>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <p className="text-xs text-[#111537]">
                  Date :{" "}
                  {data?.date || data?.createdAt ? (
                    <NuDate
                      value={data?.date || data?.createdAt}
                      format="DD-MMM-YYYY, hh:mm A"
                    />
                  ) : (
                    "N/A"
                  )}
                </p>
                <p className="text-xs text-[#111537]">
                  Created By :{" "}
                  {data?.preparedByName ||
                    data?.preparedBy ||
                    data?.createdByName ||
                    ""}
                </p>
              </div>
            </div>

            {/* <div className="flex sm:flex-col justify-between sm:justify-end w-full sm:w-[20%] items-center sm:items-end">
              <div className="flex justify-end">
                <div className="border rounded-md border-[#579BB1] text-xs inline-flex w-auto flex-shrink-0 justify-end">
                  <p className="bg-white text-black p-1 rounded-l-md">
                    Status{" "}
                  </p>
                  <p className="bg-[#579BB1] text-white p-1 items-center rounded-r-md">
                    {data?.status || "Active"}
                  </p>
                </div>
              </div>
            </div> */}
          </div>

          {/* Scrollable Main Content */}
          <div className="w-full flex-1 overflow-y-auto p-2 pb-8">
            {/* Key Information Box */}
            <div className="w-full flex flex-wrap px-2 border border-gary mt-2 rounded-lg mb-2">
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 py-1">
                {/* {(data?.prRef || data?.prNumber || data?.pr?.prNumber || data?.pr?.ref) && (
                  <div className="w-full flex px-2 items-center">
                    <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                      PR Ref
                    </p>
                    <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                      : {data?.prRef || data?.prNumber || data?.pr?.prNumber || data?.pr?.ref || "-"}
                    </p>
                  </div>
                )}

                {(data?.cqRef || data?.cqNumber || data?.customerQuotationRef || data?.cqName || data?.customerQuotation?.name || data?.customerQuotation?.ref) && (
                  <div className="w-full flex px-2 items-center">
                    <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                      CQ Ref
                    </p>
                    <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                      : {data?.cqRef || data?.cqNumber || data?.customerQuotationRef || data?.cqName || data?.customerQuotation?.name || data?.customerQuotation?.ref || "-"}
                    </p>
                  </div>
                )} */}

                <div className="w-full flex px-2 items-center">
                  <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                    Payment Terms
                  </p>
                  <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                    : {data?.paymentTerms || "100% Advance"}
                  </p>
                </div>

                <div className="w-full flex px-2 items-center">
                  <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                    Delivery Terms
                  </p>
                  <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                    : {data?.deliveryTerms || "Ex-Works"}
                  </p>
                </div>

                <div className="w-full flex px-2 items-center">
                  <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                    Lead Time
                  </p>
                  <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                    : {data?.deliveryLeadTime || "15 Days"}
                  </p>
                </div>

                <div className="w-full flex px-2 items-center">
                  <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                    Validity
                  </p>
                  <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                    : {data?.validity || "30 Days"}
                  </p>
                </div>

                <div className="w-full flex px-2 items-center">
                  <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                    Currency
                  </p>
                  <p className="w-2/3 py-2 text-xs font-semibold text-[#048178] break-words">
                    : {selectedCurrency}
                  </p>
                </div>

                <div className="w-full flex px-2 items-center">
                  <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                    VAT Status
                  </p>
                  <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                    :{" "}
                    {data?.vatApplicable
                      ? `${effectiveVatRate}% VAT Applicable`
                      : "No VAT"}
                  </p>
                </div>
              </div>
            </div>

            {/* Notes / Remarks */}
            <div className="flex gap-4 mt-3">
              <div className="w-full">
                <p className="text-xs font-medium text-[#111537] p-1">
                  Remarks & Notes
                </p>
                <div className="border border-gary min-h-[50px] max-h-24 overflow-y-scroll rounded-lg items-center">
                  <p className="text-xs p-2 break-words text-[#606279]">
                    {data?.remarks || "Thank you for your business."}
                  </p>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="w-full bg-white border border-gary mt-4 rounded-lg overflow-x-auto">
              <div className="w-full min-w-[650px]">
                <div className="w-full py-2 bg-baseColor-primary flex justify-between rounded-t-lg px-3">
                  <p className="w-[8%] text-left text-white text-xs font-medium">
                    S.No
                  </p>
                  <p className="w-[42%] text-left text-white text-xs font-medium">
                    Product
                  </p>
                  <p className="w-[12%] text-center text-white text-xs font-medium">
                    Unit
                  </p>
                  <p className="w-[12%] text-center text-white text-xs font-medium">
                    Quantity
                  </p>
                  <p className="w-[13%] text-right text-white text-xs font-medium">
                    Unit Price
                  </p>
                  <p className="w-[13%] text-right text-white text-xs font-medium">
                    Total Price
                  </p>
                </div>

                {productList.length > 0 ? (
                  productList.map((prod, idx) => {
                    const qty = Number(prod.quantity) || 0;
                    const price = Number(prod.unitPrice ?? prod.price) || 0;
                    const lineTotal =
                      prod.totalPrice ?? (qty * price);
                    const name = (
                      prod.product_name ||
                      prod.productName ||
                      prod.product?.name ||
                      prod.product?.productName ||
                      prod.name ||
                      (prod.productId
                        ? `Product #${prod.productId}`
                        : `Product #${idx + 1}`)
                    ).trim();
                    const unit =
                      prod.unit ||
                      prod.product_unit ||
                      prod.productUnit ||
                      "-";

                    return (
                      <div
                        key={idx}
                        className="w-full py-2.5 flex px-3 justify-between odd:bg-white even:bg-slate-100 items-center text-xs text-[#111537]"
                      >
                        <p className="w-[8%] text-left font-medium">
                          {idx + 1}
                        </p>
                        <p className="w-[42%] text-left font-medium break-words">
                          {name}
                        </p>
                        <p className="w-[12%] text-center font-medium text-[#606279]">
                          {unit}
                        </p>
                        <p className="w-[12%] text-center font-bold">
                          {qty}
                        </p>
                        <p className="w-[13%] text-right font-medium text-gray-700">
                          <NuAmount
                            currency={selectedCurrency}
                            value={price}
                          />
                        </p>
                        <p className="w-[13%] text-right font-bold text-[#111537]">
                          <NuAmount
                            currency={selectedCurrency}
                            value={lineTotal}
                          />
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <div className="w-full p-4 text-center text-xs text-gray-400">
                    No products mapped to this Proforma Invoice.
                  </div>
                )}

                {/* Financial Totals Summary */}
                <div className="w-full border-t border-gary px-4 py-3 flex flex-col items-end gap-1.5 bg-slate-50 rounded-b-lg">
                  <div className="flex justify-between w-64 text-xs">
                    <span className="font-medium text-[#111537]">
                      Subtotal :
                    </span>
                    <span className="font-medium text-[#606279]">
                      <NuAmount
                        currency={selectedCurrency}
                        value={subTotal}
                      />
                    </span>
                  </div>

                  {discount > 0 && (
                    <div className="flex justify-between w-64 text-xs">
                      <span className="font-medium text-[#111537]">
                        Discount (-) :
                      </span>
                      <span className="font-medium text-emerald-600">
                        <NuAmount
                          currency={selectedCurrency}
                          value={discount}
                        />
                      </span>
                    </div>
                  )}

                  {transportation > 0 && (
                    <div className="flex justify-between w-64 text-xs">
                      <span className="font-medium text-[#111537]">
                        Transportation (+) :
                      </span>
                      <span className="font-medium text-[#606279]">
                        <NuAmount
                          currency={selectedCurrency}
                          value={transportation}
                        />
                      </span>
                    </div>
                  )}
                  {
                    data?.vatApplicable &&

                    <div className="flex justify-between w-64 text-xs">
                      <span className="font-medium text-[#111537]">
                        VAT ({data?.vatApplicable ? `${effectiveVatRate}%` : "0%"}) :
                      </span>
                      <span className="font-medium text-[#606279]">
                        <NuAmount
                          currency={selectedCurrency}
                          value={vatAmount}
                        />
                      </span>
                    </div>}

                  <div className="flex justify-between w-64 text-xs font-semibold text-[#111537] border-t border-gray-300 pt-1.5">
                    <span>Grand Total :</span>
                    <span className="text-sm text-baseColor-primary">
                      <NuAmount
                        currency={selectedCurrency}
                        value={grandTotal}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (30% Sidebar) */}
        <div className="w-full lg:w-[30%] overflow-auto bg-white rounded-lg drop-shadow-lg p-2 flex flex-col gap-3">
          <div className="w-full overflow-y-auto p-1 flex flex-col gap-3">
            {/* Customer Detail Card */}
            <div className="w-full grid border border-gary rounded-lg">
              <div className="bg-baseColor-primary p-2 rounded-t-lg">
                <p className="text-xs text-white font-medium">
                  Customer Detail
                </p>
              </div>
              <div className="w-full px-2 py-1">
                <table className="w-full border-collapse text-left">
                  <tbody>
                    <TempTrCom
                      label="Customer"
                      value={
                        data?.customerName ||
                        data?.customer?.companyName ||
                        data?.customer?.name ||
                        "-"
                      }
                    />
                    {(data?.prRef || data?.prNumber || data?.pr?.prNumber || data?.pr?.ref) && (
                      <TempTrCom
                        label="PR Ref"
                        value={data?.prRef || data?.prNumber || data?.pr?.prNumber || data?.pr?.ref || "-"}
                      />
                    )}
                    {(data?.cqRef || data?.cqNumber || data?.customerQuotationRef || data?.cqName || data?.customerQuotation?.name || data?.customerQuotation?.ref) && (
                      <TempTrCom
                        label="CQ Ref"
                        value={
                          data?.cqRef ||
                          data?.cqNumber ||
                          data?.customerQuotationRef ||
                          data?.cqName ||
                          data?.customerQuotation?.name ||
                          data?.customerQuotation?.ref ||
                          "-"
                        }
                      />
                    )}
                    {(!data?.prRef && !data?.prNumber && !data?.pr?.prNumber && !data?.pr?.ref && !data?.cqRef && !data?.cqNumber && !data?.customerQuotationRef && !data?.cqName && !data?.customerQuotation?.name && !data?.customerQuotation?.ref && data?.customerPoNo) && (
                      <TempTrCom
                        label="Customer Po No"
                        value={data?.customerPoNo || ""}
                      />
                    )}
                    {formattedAddress && (
                      <TempTrCom
                        label="Address"
                        value={formattedAddress}
                      />
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bank Details Card */}
            <div className="w-full grid border border-gary mt-1 rounded-lg">
              <div className="bg-baseColor-primary p-2 rounded-t-lg">
                <p className="text-xs text-white font-medium">
                  Bank Details
                </p>
              </div>
              <div className="w-full px-2 py-1">
                <table className="w-full border-collapse text-left">
                  <tbody>
                    <TempTrCom
                      label="Bank Name"
                      value={
                        data?.bank?.bankName || data?.bankName || "-"
                      }
                    />
                    <TempTrCom
                      label="Account Name"
                      value={
                        data?.bank?.accountName ||
                        data?.accountName ||
                        "-"
                      }
                    />
                    <tr className="w-full">
                      <td className="w-[100px] py-1.5 align-top">
                        <p className="text-xs font-medium text-[#111537]">
                          Account No
                        </p>
                      </td>
                      <td className="w-[10px] py-1.5 align-top text-xs text-[#111537]">
                        :
                      </td>
                      <td className="flex-1 py-1.5 align-top">
                        <div className="flex items-center text-xs text-[#606279] break-words">
                          <span>
                            {data?.bank?.accountNumber ||
                              data?.accountNumber ||
                              "-"}
                          </span>
                          {(data?.bank?.accountNumber ||
                            data?.accountNumber) && (
                              <CopyButton
                                text={
                                  data?.bank?.accountNumber ||
                                  data?.accountNumber
                                }
                              />
                            )}
                        </div>
                      </td>
                    </tr>
                    <tr className="w-full">
                      <td className="w-[100px] py-1.5 align-top">
                        <p className="text-xs font-medium text-[#111537]">
                          IBAN
                        </p>
                      </td>
                      <td className="w-[10px] py-1.5 align-top text-xs text-[#111537]">
                        :
                      </td>
                      <td className="flex-1 py-1.5 align-top">
                        <div className="flex items-center text-xs font-mono text-[#606279] break-words">
                          <span>
                            {data?.bank?.iban || data?.iban || "-"}
                          </span>
                          {(data?.bank?.iban || data?.iban) && (
                            <CopyButton
                              text={data?.bank?.iban || data?.iban}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                    <TempTrCom
                      label="SWIFT Code"
                      value={
                        data?.bank?.swiftCode || data?.swiftCode || "-"
                      }
                    />
                  </tbody>
                </table>
              </div>
            </div>

            {/* Documents Card */}
            <div className="w-full grid border border-gary mt-1 rounded-lg">
              <p className="text-xs text-white font-medium bg-baseColor-primary p-2 rounded-t-lg">
                Documents
              </p>
              <div className="w-full flex flex-wrap overflow-y-scroll h-16 p-2">
                <div
                  onClick={() => setShowPdfModal(true)}
                  className="cursor-pointer m-1 w-auto h-[26px] flex items-center justify-between rounded p-[3px] border border-gray-300 hover:border-[#048178] bg-white transition-colors"
                  title="Click to view PDF"
                >
                  <svg
                    width="18"
                    height="18"
                    fill="#048178"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    className="mr-1.5"
                  >
                    <path d="M0 64C0 28.7 28.7 0 64 0L224 0l0 128c0 17.7 14.3 32 32 32l128 0 0 144-208 0c-35.3 0-64 28.7-64 64l0 144-48 0c-35.3 0-64-28.7-64-64L0 64zm384 64l-128 0L256 0 384 128zM176 352l32 0c30.9 0 56 25.1 56 56s-25.1 56-56 56l-16 0 0 32c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-48 0-80c0-8.8 7.2-16 16-16zm32 80c13.3 0 24-10.7 24-24s-10.7-24-24-24l-16 0 0 48 16 0zm96-80l32 0c26.5 0 48 21.5 48 48l0 64c0 26.5-21.5 48-48 48l-32 0c-8.8 0-16-7.2-16-16l0-128c0-8.8 7.2-16 16-16zm32 128c8.8 0 16-7.2 16-16l0-64c0-8.8-7.2-16-16-16l-16 0 0 96 16 0zm80-112c0-8.8 7.2-16 16-16l48 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-32 0 0 32 32 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-32 0 0 48c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-64 0-64z" />
                  </svg>
                  <p className="w-auto text-[11px] font-medium text-[#111537]">
                    {piRef}.pdf
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ModalContainer
        visiable={deleteConfirmOpen}
        title="Delete Proforma Invoice"
        content={`Are you sure you want to delete "${piRef}"? This action cannot be undone.`}
        handleClick={handleConfirmDelete}
        closeModal={() => setDeleteConfirmOpen(false)}
      />

      {/* PDF File View Modal */}
      <ModalContainer
        visiable={showPdfModal}
        title=""
        hasButton={false}
        hasContent={true}
        content={
          <div className="w-[95vw] relative h-[92vh]">
            <div className="w-full -mt-3 mb-2 flex justify-between items-center">
              <p className="font-semibold text-sm">PDF File View</p>
              <button
                className="absolute top-0 right-0 text-white bg-red-600 rounded p-1 hover:bg-red-700 transition-colors"
                onClick={() => setShowPdfModal(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="w-full h-full">
              {viewPDF ? (
                <iframe
                  src={viewPDF}
                  style={{ width: "100%", height: "100%" }}
                  frameBorder="0"
                  title="PDF File View"
                />
              ) : pdfLoading ? (
                <div className="w-full h-full flex flex-col justify-center items-center gap-2">
                  <div className="w-8 h-8 border-3 border-[#048178] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm text-gray-500 font-medium">
                    Loading PDF...
                  </p>
                </div>
              ) : (
                <div className="w-full h-full flex justify-center items-center">
                  <p className="text-sm text-gray-500">No File to View</p>
                </div>
              )}
            </div>
          </div>
        }
        closeModal={() => setShowPdfModal(false)}
      />
    </div>
  );
};

export default ViewProformaInvoice;

function TempTrCom({ label = "", value = "", subValue = "" }) {
  return (
    <tr className="w-full">
      <td className="w-[100px] py-1.5 align-top">
        <p className="text-xs font-medium text-[#111537]">{label}</p>
      </td>
      <td className="w-[10px] py-1.5 align-top text-xs text-[#111537]">:</td>
      <td className="flex-1 py-1.5 align-top">
        <p className="text-xs text-[#606279] break-words">{value || "-"}</p>
        {subValue && (
          <p className="text-xs text-[#606279] break-words mt-0.5">
            {subValue}
          </p>
        )}
      </td>
    </tr>
  );
}
